<template>
    <ion-page>
      <ion-header :translucent="false">
        <ion-toolbar>
          <ion-buttons slot="start" v-show="true">
            <ion-menu-button color="primary"></ion-menu-button>
          </ion-buttons>
          <HeaderSearch />
          <ion-title v-show="!canHeaderSearch">{{ $route.name }}</ion-title>
          <ion-buttons slot="end">
            <ion-button fill="clear" @click="goHome" aria-label="Ir al inicio">
              <ion-icon :icon="homeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      
      <ion-content :fullscreen="false" class="main-content">
        <ion-tabs>
          <ion-router-outlet class="tab-router-outlet" :key="routerKey"></ion-router-outlet>
          
          <p v-if="currentUserStore().$state.user" class="ion-text-center" style="font-size: small;">
            Hola {{ currentUserStore().$state.user?.name?.split(' ')[0] || currentUserStore().$state.user?.name || 'Usuario' }}
            <ion-icon size="small" :icon="logOutOutline" @click="userAuth().logout()" style="margin-left: 0.4rem;" />
          </p>
        </ion-tabs>

        <!-- Icono de mensajes flotante en el footer -->
        <ion-fab 
          v-if="unreadCount > 0"
          vertical="bottom" 
          horizontal="end" 
          slot="fixed"
          :class="['messages-fab', getVibrateClass()]"
        >
          <ion-fab-button 
            color="primary"
            @click="goToMessages"
          >
            <ion-icon :icon="mailOutline"></ion-icon>
          </ion-fab-button>
          <ion-badge 
            color="danger" 
            class="message-badge"
          >
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </ion-badge>
        </ion-fab>

        <!-- Icono de incidencias flotante en el footer -->
        <ion-fab 
          v-if="openIncidenciasCount > 0"
          vertical="bottom" 
          horizontal="end" 
          slot="fixed"
          :class="['incidencias-fab', { 'incidencias-fab--critical': hasCriticalIncidencias }]"
          :style="{ right: unreadCount > 0 ? '90px' : '16px' }"
        >
          <ion-fab-button 
            color="danger"
            @click="goToIncidencias"
          >
            <ion-icon :icon="warningOutline"></ion-icon>
          </ion-fab-button>
          <ion-badge 
            color="danger" 
            class="incidencia-badge"
          >
            {{ openIncidenciasCount > 99 ? '99+' : openIncidenciasCount }}
          </ion-badge>
        </ion-fab>
      </ion-content>
    </ion-page>
  </template>
  
  <script lang="ts">
    import { IonToolbar,IonPage, IonTabs, IonRouterOutlet, IonIcon,IonTitle,IonHeader,IonMenuButton,IonButtons,IonButton,IonContent, IonFab, IonFabButton, IonBadge } from '@ionic/vue';
    import { homeOutline, walletOutline, peopleCircleOutline,logoWhatsapp, logOutOutline, mailOutline, warningOutline } from 'ionicons/icons';
    import userAuth from '@/composables/userAuth';
    import { currentUserStore } from '@/stores/currentUser';
    import HeaderSearch from '@/components/HeaderSearch.vue';
    import { computed, onMounted, onUnmounted } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { useUnreadMessages } from '@/composables/unreadMessages';
    import { useOpenIncidencias } from '@/composables/unreadIncidencias';


    export default {
      components: { IonToolbar,IonPage, IonTabs, IonRouterOutlet, IonIcon,IonTitle,IonHeader,IonMenuButton,IonButtons,IonButton,IonContent, IonFab, IonFabButton, IonBadge, HeaderSearch },
      setup() {
        const route = useRoute();
        const router = useRouter();
        const userStore = currentUserStore();
        const { unreadCount, startPolling, stopPolling } = useUnreadMessages();
        const { openIncidenciasCount, hasCriticalIncidencias, startPolling: startIncidenciasPolling, stopPolling: stopIncidenciasPolling } = useOpenIncidencias();

        const canHeaderSearch = computed(() => {
          const perms = userStore.permissionNames;
          return perms.includes('user list')
            || perms.includes('procesos list')
            || perms.includes('incidencias list');
        });
        
        const routerKey = computed(() => {
          const routeName = String(route.name || '');
          const procesoId = String(route.params.proceso_id || '');
          return `${routeName}-${procesoId}`;
        });

        const goToMessages = () => {
          router.push('/organizacion/Mensajes');
        };

        const goToIncidencias = () => {
          router.push('/organizacion/incidencias');
        };

        const goHome = () => {
          router.push('/');
        };

        const getVibrateClass = () => {
          if (unreadCount.value === 0) return '';
          if (unreadCount.value >= 20) return 'vibrate-urgent';
          if (unreadCount.value >= 10) return 'vibrate-high';
          if (unreadCount.value >= 5) return 'vibrate-medium';
          return 'vibrate-low';
        };

        onMounted(() => {
          startPolling(30000); // Polling cada 30 segundos
          startIncidenciasPolling(60000); // Polling cada 60 segundos
        });

        onUnmounted(() => {
          stopPolling();
          stopIncidenciasPolling();
        });



        return {
          walletOutline,
          peopleCircleOutline,
          homeOutline,
          userAuth,
          logoWhatsapp,
          currentUserStore,
          canHeaderSearch,
          logOutOutline,
          routerKey,
          mailOutline,
          warningOutline,
          unreadCount,
          openIncidenciasCount,
          hasCriticalIncidencias,
          goToMessages,
          goToIncidencias,
          goHome,
          getVibrateClass
        };       
      },
    };
  </script>

<style scoped>
  ion-label{
    font-size: 0.8rem;
  }

  ion-tab-bar{
    height: 4rem;
  }

  ion-toolbar {
    --min-height: 56px;
  }

  ion-toolbar :deep(.header-search) {
    width: 100%;
    max-width: 640px;
  }

  /* Asegurar que el contenido aparezca debajo del header */
  .main-content {
    --padding-top: 0;
    --padding-bottom: 0;
  }

  ion-tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  /* La ruta hija (ion-page) ocupa el alto y hace scroll en su propio ion-content */
  .tab-router-outlet {
    display: block;
    width: 100%;
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;
  }

  /* Forzar que el contenido respete el header */
  ion-content {
    --offset-top: 0;
  }

  /* Estilos para el botón flotante de mensajes */
  .messages-fab {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 999;
  }

  .messages-fab ion-fab-button {
    --border-radius: 50%;
    --box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    --transition: all 0.2s ease-in-out;
    --background: var(--ion-color-primary);
    --background-activated: var(--ion-color-primary-shade);
    --background-focused: var(--ion-color-primary-shade);
    --color: white;
    width: 56px;
    height: 56px;
  }

  .messages-fab ion-fab-button:hover {
    --box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }

  .messages-fab ion-fab-button ion-icon {
    font-size: 24px;
  }

  /* Animaciones de vibración basadas en cantidad de mensajes */
  .messages-fab.vibrate-low ion-fab-button {
    animation: vibrate-low 2s infinite;
  }

  .messages-fab.vibrate-medium ion-fab-button {
    animation: vibrate-medium 1.5s infinite;
  }

  .messages-fab.vibrate-high ion-fab-button {
    animation: vibrate-high 1s infinite;
  }

  .messages-fab.vibrate-urgent ion-fab-button {
    animation: vibrate-urgent 0.8s infinite;
  }

  /* Keyframes para diferentes intensidades de vibración */
  @keyframes vibrate-low {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-1px); }
    75% { transform: translateX(1px); }
  }

  @keyframes vibrate-medium {
    0%, 100% { transform: translateX(0); }
    10% { transform: translateX(-2px); }
    20% { transform: translateX(2px); }
    30% { transform: translateX(-2px); }
    40% { transform: translateX(2px); }
    50% { transform: translateX(-1px); }
    60% { transform: translateX(1px); }
    70% { transform: translateX(-1px); }
    80% { transform: translateX(1px); }
    90% { transform: translateX(0); }
  }

  @keyframes vibrate-high {
    0%, 100% { transform: translate(0); }
    10% { transform: translate(-3px, -1px); }
    20% { transform: translate(3px, 1px); }
    30% { transform: translate(-3px, 1px); }
    40% { transform: translate(3px, -1px); }
    50% { transform: translate(-2px, -2px); }
    60% { transform: translate(2px, 2px); }
    70% { transform: translate(-1px, -3px); }
    80% { transform: translate(1px, 3px); }
    90% { transform: translate(-1px, 1px); }
  }

  @keyframes vibrate-urgent {
    0%, 100% { transform: translate(0) rotate(0deg); }
    5% { transform: translate(-4px, -2px) rotate(-1deg); }
    10% { transform: translate(4px, 2px) rotate(1deg); }
    15% { transform: translate(-4px, 2px) rotate(-1deg); }
    20% { transform: translate(4px, -2px) rotate(1deg); }
    25% { transform: translate(-3px, -3px) rotate(-2deg); }
    30% { transform: translate(3px, 3px) rotate(2deg); }
    35% { transform: translate(-2px, -4px) rotate(-1deg); }
    40% { transform: translate(2px, 4px) rotate(1deg); }
    45% { transform: translate(-1px, -2px) rotate(-0.5deg); }
    50% { transform: translate(1px, 2px) rotate(0.5deg); }
    55% { transform: translate(-2px, 1px) rotate(-1deg); }
    60% { transform: translate(2px, -1px) rotate(1deg); }
    65% { transform: translate(-1px, -1px) rotate(-0.5deg); }
    70% { transform: translate(1px, 1px) rotate(0.5deg); }
    75% { transform: translate(-1px, 2px) rotate(-0.5deg); }
    80% { transform: translate(1px, -2px) rotate(0.5deg); }
    85% { transform: translate(-1px, 1px) rotate(0deg); }
    90% { transform: translate(1px, -1px) rotate(0deg); }
    95% { transform: translate(0, 1px) rotate(0deg); }
  }

  .messages-fab .message-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    min-width: 22px;
    height: 22px;
    font-size: 11px;
    font-weight: bold;
    z-index: 1001;
    border: 2px solid white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    --background: #eb445a;
    --color: white;
  }

  /* Estilos para el botón flotante de incidencias */
  .incidencias-fab {
    position: fixed;
    bottom: 16px;
    transition: right 0.3s ease;
  }

  .incidencias-fab--critical {
    animation: critical-fab-pulse 1.2s ease-in-out infinite;
  }

  @keyframes critical-fab-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.08); }
  }

  .incidencias-fab .incidencia-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    min-width: 22px;
    height: 22px;
    font-size: 11px;
    font-weight: bold;
    z-index: 1001;
    border: 2px solid white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    --background: #eb445a;
    --color: white;
  }
</style>